import React, { useEffect, useRef } from 'react';
import { Field, useFormikContext } from 'formik';
import DescriptionIcon from '@mui/icons-material/Description';

import {
  AddButtonUi,
  AutoCompleteSelectUI,
  ButtonUI,
  DatePickerUI,
  FormErrorUI,
  FormLabelUI,
  InputUI,
} from '../Interface';
import companyService from '../../service/company.service';
import { generateAutocompleteItems } from '../../utils/helper';
import useConfig from '../../context/ConfigContext';
import { DocumentDirection } from '../../config/DocumentDirection';
import { DocStatus, DocStatusList } from '../../config/document';
import exchangeRateService from '../../service/exchangeRate.service';
import { IconButton } from '@mui/material';
import { DocumentType } from '../../config/DocumentType';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import useParam from '../../context/ParamContext';
import { FromType, PaymentDeadlineBasisType } from '../../config/paymentMethod';
import dayjs from 'dayjs';

const DocumentFields = ({
  viewField,
  autocompleteOptions,
  selectedValues,
  autocompleteData,
  documentData,
  selectedCompanyState,
  companyContactsState,
  setSettlementDialogOpen,
  docTypeId,
  getDownPaymentDoc,
  editButtonVisible,
  currentDocType,
  setCompanyDialogOpen,
  setBulkUpdateDialogState,
}) => {
  const { params } = useParam();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const { config, getConfig } = useConfig();
  const { selectedCompany, setSelectedCompany } = selectedCompanyState;
  const { setCompanyContacts } = companyContactsState;
  const isCopyMode = selectedValues?.mode === 'copy';
  const hasSelectedValues = Boolean(selectedValues && Object.keys(selectedValues).length > 0);
  const isLoadedExistingDoc = hasSelectedValues && !isCopyMode;
  const initialLoadedKeyRef = useRef(null);
  const initialLoadSkippedRef = useRef(false);
  const lastHandledKeyRef = useRef(null);
  const lastHandledConfigCurrencyRef = useRef(null);
  const showCompanyFields = Boolean(currentDocType?.company || currentDocType?.cooperation);
  const companyLabel = currentDocType?.cooperation
    ? 'Partner'
    : documentData.direction === DocumentDirection.INCOMING
      ? 'Szállító'
      : 'Vevő';

  const isInventoryAccountingDoc = Boolean(documentData?.inventoryAccounting);
  const isNewDocument = !values?.docId && (!selectedValues || Object.keys(selectedValues).length === 0);
  const isDocDateLocked = isInventoryAccountingDoc && !isNewDocument && values?.docStatus !== DocStatus.PLANNED;

  const getCompanyData = async (companyId) => {
    const selCom = await companyService.getCompanyById(companyId, true);
    setSelectedCompany(selCom);
  };

  const getDailyRate = async () => {
    const rateFilter = { currencyCode: values.currencyCode, rateDate: values.docDate };
    setFieldValue('exchangeRate', '');
    const rate = (await exchangeRateService.getDailyRate(rateFilter)) ?? null;
    if (rate) {
      setFieldValue('exchangeRate', parseFloat(rate.rate));
    }
  };

  useEffect(() => {
    if (!Object.keys(config).length) getConfig();
  }, []);

  useEffect(() => {
    if (!hasSelectedValues) {
      initialLoadedKeyRef.current = null;
      initialLoadSkippedRef.current = false;
      lastHandledKeyRef.current = null;
      lastHandledConfigCurrencyRef.current = null;
      return;
    }

    initialLoadedKeyRef.current = `${selectedValues?.currencyCode ?? ''}|${
      selectedValues?.docDate ? dayjs(selectedValues.docDate).format('YYYY-MM-DD') : ''
    }`;
    initialLoadSkippedRef.current = false;
    lastHandledKeyRef.current = null;
    lastHandledConfigCurrencyRef.current = null;
  }, [hasSelectedValues, selectedValues?.docId]);

  useEffect(() => {
    if (values.companyId) {
      const selectedCompany = autocompleteData?.company?.find((i) => i.id === values.companyId);

      setFieldValue('companyCode', selectedCompany?.code);
      setFieldValue('companyName', selectedCompany?.name);

      getCompanyData(values.companyId);
    } else {
      setSelectedCompany(null);
      setCompanyContacts([]);
    }
  }, [values.companyId]);

  useEffect(() => {
    if (Object.keys(values).length === 0) return;
    if (!selectedCompany) {
      const findCurrencyByDefPl = autocompleteData?.priceList?.find((p) => p.id === config.defaultPriceList);

      setFieldValue('companyCode', '');
      setFieldValue('companyName', '');
      setFieldValue('companyContactId', null);
      setFieldValue('currencyCode', findCurrencyByDefPl?.currencyCode || config.currencyCode || null);
      setFieldValue('paymentMethodId', config.defPaymentMethod ?? null);
      setFieldValue('salesOrPurchasePersonId', null);
      setFieldValue('deliveryMethodId', null);
      setFieldValue('incotermCode', null);
      setFieldValue('priceListId', config.defaultPriceList ?? null);
      setFieldValue('langCode', null);
      setFieldValue('hunTaxNumber', null);
      setFieldValue('euTaxNumber', null);
      setFieldValue('foreignTaxNumber', null);
      setFieldValue('billingAdressId', null);
      setFieldValue('billingCountry', '');
      setFieldValue('billingProvince', '');
      setFieldValue('billingPostCode', '');
      setFieldValue('billingCity', '');
      setFieldValue('billingStreetAddress', '');
      setFieldValue('shippingAdressId', null);
      setFieldValue('shippingCountry', '');
      setFieldValue('shippingProvince', '');
      setFieldValue('shippingPostCode', '');
      setFieldValue('shippingCity', '');
      setFieldValue('shippingStreetAddress', '');
      setFieldValue('shortName', '');
      setFieldValue('incotermCity', '');
      setFieldValue('companyWarning', '');
      return;
    }

    setCompanyContacts(generateAutocompleteItems(selectedCompany.companyContact, 'name', 'id'));

    if (!isLoadedExistingDoc) {
      const defContact = selectedCompany.companyContact.find((cc) => cc.default);
      setFieldValue('companyContactId', defContact?.id ?? selectedCompany?.companyContact[0]?.id ?? null);

      setFieldValue('langCode', selectedCompany.languageCode);
      setFieldValue('hunTaxNumber', selectedCompany.hunTaxNumber);
      setFieldValue('euTaxNumber', selectedCompany.euTaxNumber);
      setFieldValue('foreignTaxNumber', selectedCompany.foreignTaxNumber);
      setFieldValue('billingAdressId', null);
      setFieldValue('billingCountry', '');
      setFieldValue('billingProvince', '');
      setFieldValue('billingPostCode', '');
      setFieldValue('billingCity', '');
      setFieldValue('billingStreetAddress', '');
      setFieldValue('shippingAdressId', null);
      setFieldValue('shippingCountry', '');
      setFieldValue('shippingProvince', '');
      setFieldValue('shippingPostCode', '');
      setFieldValue('shippingCity', '');
      setFieldValue('shippingStreetAddress', '');
      setFieldValue('shortName', selectedCompany.shortName);
      setFieldValue('incotermCity', selectedCompany.incotermCity);
      setFieldValue('companyWarning', selectedCompany.companyWarning);

      if (documentData.direction === DocumentDirection.INCOMING) {
        const crncy = selectedCompany.supplierCurrencyCode || config.currencyCode;
        setFieldValue('currencyCode', crncy);
        setFieldValue('paymentMethodId', selectedCompany.supplierPaymentMethodId);
        setFieldValue('salesOrPurchasePersonId', selectedCompany.purchasePersonId);
        setFieldValue('deliveryMethodId', selectedCompany.supplierDeliveryMethodId);
        setFieldValue('incotermCode', selectedCompany.supplierIncotermCode);
        setFieldValue('priceListId', selectedCompany.supplierPriceListId);
      } else if (documentData.direction === DocumentDirection.OUTGOING) {
        const crncy = selectedCompany.customerCurrencyCode || config.currencyCode;
        setFieldValue('currencyCode', crncy);
        setFieldValue('paymentMethodId', selectedCompany.customerPaymentMethodId);
        setFieldValue('salesOrPurchasePersonId', selectedCompany.salesPersonId);
        setFieldValue('deliveryMethodId', selectedCompany.customerDeliveryMethodId);
        setFieldValue('incotermCode', selectedCompany.customerIncotermCode);
        setFieldValue('priceListId', selectedCompany.customerPriceListId);
      } else {
        setFieldValue('currencyCode', config.currencyCode);
      }
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (Object.keys(values).length === 0) return;

    const currentKey = `${values?.currencyCode ?? ''}|${values?.docDate ? dayjs(values.docDate).format('YYYY-MM-DD') : ''}`;

    if (lastHandledKeyRef.current === currentKey && lastHandledConfigCurrencyRef.current === config.currencyCode) {
      return;
    }

    const isInitialLoadedState = hasSelectedValues && currentKey === initialLoadedKeyRef.current;

    if (!initialLoadSkippedRef.current && isInitialLoadedState) {
      initialLoadSkippedRef.current = true;
      lastHandledKeyRef.current = currentKey;
      lastHandledConfigCurrencyRef.current = config.currencyCode;
      return;
    }

    if (!values.docDate) {
      lastHandledKeyRef.current = currentKey;
      lastHandledConfigCurrencyRef.current = config.currencyCode;
      setFieldValue('exchangeRate', '');
      addNotification({
        content: 'Teljesítés dátuma nélkül nem tudjuk az árfolyamot betölteni!',
        type: NotificationType.ERROR,
      });
      return;
    }

    if (values.currencyCode && values.currencyCode !== config.currencyCode) {
      getDailyRate();
    } else {
      setFieldValue('exchangeRate', '');
    }

    lastHandledKeyRef.current = currentKey;
    lastHandledConfigCurrencyRef.current = config.currencyCode;
  }, [values.currencyCode, values.docDate, config.currencyCode, hasSelectedValues]);

  const handleFieldChange = async (field, newVal) => {
    // Normalize empty-string values to null to avoid wiping defaults
    let selected = newVal?.value ?? null;
    if (selected === '') selected = null;
    await setFieldValue(field, selected);
    await validateField(field);
  };

  const isSameDayValue = (leftValue, rightValue) => {
    if (!leftValue && !rightValue) return true;
    if (!leftValue || !rightValue) return false;

    return dayjs(leftValue).isSame(dayjs(rightValue), 'day');
  };

  const handleBulkUpdatableFieldChange = async ({
    headerField,
    value,
    getLineFieldPath,
    getLineFieldValue,
    dialogTitle,
    dialogMessage,
    confirmButtonText = 'Sorok módosítása',
    areEqual = (leftValue, rightValue) => leftValue === rightValue,
  }) => {
    const previousValue = values?.[headerField] ?? null;

    await handleFieldChange(headerField, { value });

    const lines = Array.isArray(values.docLine) ? values.docLine : [];
    if (lines.length === 0 || areEqual(previousValue, value)) {
      return;
    }

    const hasDifferentLineValue = lines.some((line, index) => !areEqual(getLineFieldValue(line, index), value));
    if (!hasDifferentLineValue || typeof setBulkUpdateDialogState !== 'function') {
      return;
    }

    setBulkUpdateDialogState({
      open: true,
      title: dialogTitle,
      message: dialogMessage,
      confirmButtonText,
      value,
      fieldPaths: lines.map((_line, index) => getLineFieldPath(index)),
      setFieldValue,
      validateField,
    });
  };

  const handleCalcDueDate = async (issueDate, docDate, paymentMethodId) => {
    if (!paymentMethodId || !currentDocType.invoice) return;

    const paymentMethod = autocompleteData.paymentMethod.find((method) => method.id === paymentMethodId);
    if (!paymentMethod) return;

    const defDate =
      paymentMethod.paymentDeadlineBasis === PaymentDeadlineBasisType.DATEOFCOMPLETION ? docDate : issueDate;

    if (!defDate) return;

    const calcDate =
      paymentMethod.from === FromType.MONTHEND ? dayjs(defDate).add(1, 'month').startOf('month') : dayjs(defDate);

    const day = Number(paymentMethod.day ?? 0);
    const dueDate = paymentMethod.from === FromType.MONTHEND ? calcDate.add(day - 1, 'day') : calcDate.add(day, 'day');

    await handleBulkUpdatableFieldChange({
      headerField: 'dueDate',
      value: dueDate,
      getLineFieldPath: (index) => `docLine.${index}.dueDate`,
      getLineFieldValue: (line) => line?.dueDate ?? null,
      dialogTitle: 'Tételsorok határideje',
      dialogMessage: 'A fejléci határidő módosult. Frissítsük a tételsorok határidejét is?',
      confirmButtonText: 'Sorok frissítése',
      areEqual: isSameDayValue,
    });
  };

  return (
    <>
      <div className="grid grid-cols-5 gap-x-4 gap-y-4 mx-4 mb-2">
        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="docNum"
            as={InputUI}
            fullWidth
            disabled
            label={<FormLabelUI text="Bizonylatszám" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.docNum} />}
            InputLabelProps={{ shrink: Boolean(values.docNum) }}
          />
        </div>
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="docStatus"
            component={AutoCompleteSelectUI}
            fullWidth
            disabled={true}
            label={<FormLabelUI text="Státusz" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.docStatus} />}
            InputLabelProps={{ shrink: Boolean(values.docStatus) }}
            onChange={(_e, newVal) => handleFieldChange('docStatus', newVal)}
            selectedValue={values.docStatus}
            selectedLabelValue={DocStatusList?.find((p) => p.value === values.docStatus)?.label}
            dataset={DocStatusList}
          />
        </div>

        <div className="col-span-4 md:col-span-1">
          <Field
            type="date"
            name="docDate"
            component={DatePickerUI}
            value={values.docDate}
            fullWidth
            label={<FormLabelUI text="Teljesítés dátuma" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.docDate} />}
            onChange={(date) => {
              handleFieldChange('docDate', { value: date });
              handleCalcDueDate(values.issueDate, date, values.paymentMethodId);
            }}
            disabled={viewField || isDocDateLocked}
            InputLabelProps={{ shrink: Boolean(values.docDate) }}
          />
        </div>

        <div className="col-span-4 md:col-span-1">
          <Field
            type="date"
            name="issueDate"
            component={DatePickerUI}
            value={values.issueDate}
            fullWidth
            label={<FormLabelUI text="Kiállítás dátuma" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.issueDate} />}
            onChange={(date) => {
              handleFieldChange('issueDate', { value: date });
              handleCalcDueDate(date, values.docDate, values.paymentMethodId);
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: Boolean(values.issueDate) }}
          />
        </div>

        <div className="col-span-4 md:col-span-1">
          <Field
            type="date"
            name="dueDate"
            component={DatePickerUI}
            value={values.dueDate}
            fullWidth
            label={<FormLabelUI text="Határidő" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.dueDate} />}
            onChange={(date) =>
              handleBulkUpdatableFieldChange({
                headerField: 'dueDate',
                value: date,
                getLineFieldPath: (index) => `docLine.${index}.dueDate`,
                getLineFieldValue: (line) => line?.dueDate ?? null,
                dialogTitle: 'Tételsorok határideje',
                dialogMessage: 'A fejléci határidő módosult. Frissítsük a tételsorok határidejét is?',
                confirmButtonText: 'Sorok frissítése',
                areEqual: isSameDayValue,
              })
            }
            disabled={viewField}
            InputLabelProps={{ shrink: Boolean(values.dueDate) }}
          />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-x-4 gap-y-4 mx-4 mb-2">
        {showCompanyFields && (
          <>
            <div className="col-span-4 md:col-span-1">
              <div className="flex items-center">
                <Field
                  type="autocomplete"
                  name="companyId"
                  component={AutoCompleteSelectUI}
                  fullWidth
                  label={<FormLabelUI text={companyLabel} />}
                  variant="standard"
                  helperText={<FormErrorUI message={errors.companyId} />}
                  onChange={async (_e, newVal) => handleFieldChange('companyId', newVal)}
                  disabled={viewField}
                  InputLabelProps={{ shrink: Boolean(values.companyId) }}
                  selectedValue={values.companyId}
                  selectedLabelValue={
                    selectedValues?.company?.name ||
                    autocompleteData.company.find((cm) => cm.id === values.companyId)?.name
                  }
                  dataset={autocompleteOptions?.company}
                />
                <AddButtonUi onClick={() => setCompanyDialogOpen(true)} disabled={viewField} />
              </div>
            </div>

            <div className="col-span-4 md:col-span-1">
              <Field
                type="text"
                name="companyCode"
                as={InputUI}
                fullWidth
                disabled={true}
                label={<FormLabelUI text="Partner kód" />}
                variant="standard"
                helperText={<FormErrorUI message={errors.companyCode} />}
                InputLabelProps={{ shrink: Boolean(values.companyCode) }}
              />
            </div>

            <div className="col-span-4 md:col-span-1">
              <Field
                type="text"
                name="companyName"
                as={InputUI}
                fullWidth
                disabled={true}
                label={<FormLabelUI text="Partner név" />}
                variant="standard"
                helperText={<FormErrorUI message={errors.companyName} />}
                InputLabelProps={{ shrink: Boolean(values.companyName) }}
              />
            </div>
          </>
        )}
        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="reference"
            as={InputUI}
            fullWidth
            disabled={viewField}
            label={<FormLabelUI text="Hivatkozási szám" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.reference} />}
            InputLabelProps={{ shrink: Boolean(values.reference) }}
          />
        </div>
        {currentDocType?.employee && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="employeeId"
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Dolgozó" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.employeeId} />}
              onChange={(_e, newVal) => handleFieldChange('employeeId', newVal)}
              disabled={viewField}
              InputLabelProps={{ shrink: Boolean(values.employeeId) }}
              selectedValue={values.employeeId}
              selectedLabelValue={
                selectedValues?.employee?.name ||
                autocompleteData?.employee?.find((employee) => employee.id === values.employeeId)?.name
              }
              dataset={autocompleteOptions?.employee}
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-4 gap-x-4 gap-y-4 mx-4 mb-2">
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="paymentMethodId"
            component={AutoCompleteSelectUI}
            fullWidth
            disabled={viewField}
            label={<FormLabelUI text="Fizetési mód" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.paymentMethodId} />}
            InputLabelProps={{ shrink: Boolean(values.paymentMethodId) }}
            onChange={(_e, newVal) => {
              handleFieldChange('paymentMethodId', newVal);
              handleCalcDueDate(values.issueDate, values.docDate, newVal?.value ?? null);
            }}
            selectedValue={values.paymentMethodId}
            selectedLabelValue={selectedValues?.paymentMethod?.name}
            dataset={autocompleteOptions?.paymentMethod}
          />
        </div>

        {currentDocType?.project && (
          <>
            <div className="col-span-4 md:col-span-1">
              <Field
                type="autocomplete"
                name="projectId"
                component={AutoCompleteSelectUI}
                fullWidth
                label={<FormLabelUI text="Projekt" />}
                variant="standard"
                helperText={<FormErrorUI message={errors.projectId} />}
                onChange={(_e, newVal) => {
                  handleFieldChange('projectId', newVal);
                  const findedProject = autocompleteData?.project.find((p) => p.projectId === (newVal?.value ?? null));
                  handleFieldChange('projectOrderNumber', { value: findedProject?.orderNumber ?? '' });
                }}
                disabled={viewField}
                InputLabelProps={{ shrink: Boolean(values.projectId) }}
                selectedValue={values.projectId}
                selectedLabelValue={selectedValues?.project?.projectName}
                dataset={autocompleteOptions?.project}
              />
            </div>
            <div className="col-span-4 md:col-span-1">
              <Field
                type="autocomplete"
                name="projectOrderNumber"
                component={InputUI}
                fullWidth
                label={<FormLabelUI text={getProjectOrderNumberName(params)} />}
                variant="standard"
                helperText={<FormErrorUI message={errors.projectOrderNumber} />}
                disabled={true}
                InputLabelProps={{ shrink: values.projectOrderNumber !== '' }}
                value={values.projectOrderNumber}
              />
            </div>
          </>
        )}

        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="costCenterCode"
            component={AutoCompleteSelectUI}
            fullWidth
            disabled={viewField}
            label={<FormLabelUI text="Költséghely" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.costCenterCode} />}
            InputLabelProps={{ shrink: Boolean(values.costCenterCode) }}
            onChange={(_e, newVal) => handleFieldChange('costCenterCode', newVal)}
            selectedValue={values.costCenterCode}
            selectedLabelValue={selectedValues?.costCenter?.name}
            dataset={autocompleteOptions?.costCenter}
          />
        </div>

        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="currencyCode"
            component={AutoCompleteSelectUI}
            fullWidth
            disabled={viewField}
            label={<FormLabelUI text="Pénznem" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.currencyCode} />}
            InputLabelProps={{ shrink: Boolean(values.currencyCode) }}
            selectedValue={values.currencyCode}
            selectedLabelValue={autocompleteData?.currency?.find((p) => p.code === values.currencyCode)?.code}
            onChange={(_e, newVal) => handleFieldChange('currencyCode', newVal)}
            dataset={autocompleteOptions?.currency}
          />
        </div>

        {config?.currencyCode !== values.currencyCode && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="number"
              name="exchangeRate"
              as={InputUI}
              fullWidth
              disabled={viewField}
              label={<FormLabelUI text="Árfolyam" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.exchangeRate} />}
              InputLabelProps={{ shrink: Boolean(values.exchangeRate) }}
            />
          </div>
        )}

        {showCompanyFields && parseInt(docTypeId) === DocumentType.ARInvoice && (
          <div className="pt-3">
            <ButtonUI
              text="Előleg"
              className="bg-primary"
              size="sm"
              disabled={!values.companyId || !editButtonVisible || viewField}
              onClick={() => getDownPaymentDoc(values.companyId)}
            />
          </div>
        )}

        {documentData.enableSettlement && (
          <div className="col-span-4 md:col-span-2">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Field
                  type="number"
                  name="openAmount"
                  as={InputUI}
                  fullWidth
                  label={<FormLabelUI text="Kintlévőség" />}
                  variant="standard"
                  disabled={true}
                  InputLabelProps={{ shrink: Boolean(values.openAmount) }}
                />
              </div>
              <IconButton
                onClick={() => setSettlementDialogOpen(true)}
                disabled={!viewField && !Object.keys(selectedValues).length}
              >
                <DescriptionIcon />
              </IconButton>
            </div>
          </div>
        )}
        {documentData.additionalCost && (
          <>
            <div className="col-span-4 md:col-span-1">
              <Field
                type="number"
                name="extraCostTotal"
                as={InputUI}
                fullWidth
                disabled={viewField}
                label={<FormLabelUI text="Beszerzési mellékköltség saját pénznemben" />}
                variant="standard"
                helperText={<FormErrorUI message={errors.extraCostTotal} />}
                InputLabelProps={{ shrink: Boolean(values.extraCostTotal) }}
              />
            </div>
            <div className="col-span-4 md:col-span-1">
              <Field
                type="text"
                name="extraCostNote "
                as={InputUI}
                fullWidth
                disabled={viewField}
                label={<FormLabelUI text="Beszerzési mellékköltség leírás" />}
                variant="standard"
                helperText={<FormErrorUI message={errors.extraCostNote} />}
                InputLabelProps={{ shrink: Boolean(values.extraCostNote) }}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default DocumentFields;
